import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { IMAGES } from "@/lib/constants";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Programmes", path: "/programmes" },
  { name: "Impact", path: "/impact" },
  { name: "Get Involved", path: "/get-involved" },
  { name: "Stories", path: "/stories" },
  { name: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const isHome = location.pathname === "/";
  const navBg = scrolled || !isHome
    ? "bg-[#F0F7F4]/95 backdrop-blur-xl shadow-sm border-b border-[#2D6A4F]/10"
    : "bg-transparent";
  const textColor = scrolled || !isHome ? "text-[#1B4332]" : "text-white";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${navBg}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img
              src={IMAGES.logo}
              alt="MBT Welfare & Development Trust"
              className="h-12 md:h-14 w-auto"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-3 py-2 rounded-lg text-sm font-medium font-outfit transition-all
                  ${location.pathname === link.path
                    ? `${scrolled || !isHome ? 'text-[#D4A843] bg-[#1B4332]/5' : 'text-[#D4A843]'}`
                    : `${textColor} hover:text-[#D4A843]`
                  }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 rounded-lg ${textColor}`}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          data-testid="mobile-nav"
          className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-[#2D6A4F]/10 shadow-lg"
        >
          <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-3 rounded-lg text-base font-medium font-outfit transition-all
                  ${location.pathname === link.path
                    ? 'text-[#D4A843] bg-[#1B4332]/5'
                    : 'text-[#1B4332] hover:bg-[#1B4332]/5'
                  }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
